import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import StructuredData from '../../components/feature/StructuredData';
import Concerns from './components/Concerns';
import WhyDifficult from './components/WhyDifficult';
import FourMethods from './components/FourMethods';
import LawyerBenefits from './components/LawyerBenefits';
import Support from './components/Support';
import Flow from './components/Flow';
import FAQ from './components/FAQ';
import CTA from './components/CTA';

const KyoyuMeigi = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://hudosan-shobun.com";

  useEffect(() => {
    document.title = '共有名義・相続未分割の不動産処分｜愛知県の弁護士が解決';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '共有名義で売れない不動産、遺産分割が終わっていない土地でお困りですか？共有者との交渉から共有物分割請求訴訟まで、愛知県の弁護士が確実に解決へ導きます。初回相談無料。成功報酬型で安心。');
    }
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "name": "冨田・島岡法律事務所 - 共有名義・相続未分割不動産処分サービス",
    "description": "共有名義の不動産が売れない、相続で遺産分割が終わっていない土地を手放したい方へ。共有者との交渉から共有物分割請求まで、弁護士が処分をサポートします。",
    "url": siteUrl + "/kyoyu-meigi",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "栄2-2-17 名古屋情報センタービル6階",
      "addressLocality": "名古屋市中区",
      "addressRegion": "愛知県",
      "postalCode": "460-0008",
      "addressCountry": "JP"
    },
    "areaServed": [
      { "@type": "State", "name": "愛知県" },
      { "@type": "State", "name": "岐阜県" },
      { "@type": "State", "name": "三重県" },
      { "@type": "State", "name": "静岡県" }
    ],
    "serviceType": ["共有不動産処分", "相続未分割不動産処分", "共有物分割請求", "遺産分割協議"],
    "priceRange": "$$",
    "telephone": "052-588-0874",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    ]
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "共有者の一人と連絡が取れません。それでも処分できますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "可能です。不在者財産管理人の選任申立てや、共有物分割請求訴訟などの方法があります。共有者と連絡が取れない場合でも、法的手続きを通じて不動産の処分を進めることができます。"
        }
      },
      {
        "@type": "Question",
        "name": "遺産分割協議が終わっていませんが、相談できますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "はい、もちろん可能です。遺産分割協議のサポートから対応いたします。相続人の確定、遺産の調査、協議書の作成まで、一貫してサポートいたします。"
        }
      },
      {
        "@type": "Question",
        "name": "相談だけでも大丈夫ですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "初回相談は無料です。ご依頼を強制することはありませんので、お気軽にご相談ください。"
        }
      },
      {
        "@type": "Question",
        "name": "共有物分割請求とは何ですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "共有物分割請求とは、共有者間で話し合いがまとまらない場合に、裁判所を通じて共有状態を解消する手続きです。裁判所が、不動産を現物分割、代償分割、または競売による換価分割のいずれかの方法で分割を命じます。"
        }
      },
      {
        "@type": "Question",
        "name": "費用はどのくらいかかりますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ケースにより異なりますが、初回相談は無料です。具体的な費用については、状況をお伺いした上でお見積りいたします。着手金、成功報酬、実費などの内訳を明確にご説明いたします。"
        }
      }
    ]
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "ホーム",
        "item": siteUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "共有名義・相続未分割不動産",
        "item": siteUrl + "/kyoyu-meigi"
      }
    ]
  };

  const webPageData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "共有名義・相続未分割の不動産を処分する方法｜弁護士が解説",
    "description": "共有名義の不動産が売れない、相続で遺産分割が終わっていない土地を手放したい方へ。共有者との交渉から共有物分割請求まで、弁護士が処分をサポートします。",
    "url": siteUrl + "/kyoyu-meigi",
    "isPartOf": {
      "@type": "WebSite",
      "name": "冨田・島岡法律事務所",
      "url": siteUrl
    },
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", "h2"]
    }
  };

  return (
    <div className="min-h-screen">
      <StructuredData data={structuredData} />
      <StructuredData data={faqStructuredData} />
      <StructuredData data={breadcrumbData} />
      <StructuredData data={webPageData} />
      
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              共有名義・相続未分割の<br />不動産を処分する方法
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8">
              共有者との交渉から共有物分割請求まで<br />
              弁護士が確実に解決へ導きます
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#cta"
                className="inline-flex items-center justify-center px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl mr-2"></i>
                無料相談を予約する
              </a>
              <a
                href="#concerns"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg border-2 border-white/30 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                詳しく見る
              </a>
            </div>
          </div>
        </div>
      </section>

      <Concerns />
      <WhyDifficult />
      <FourMethods />
      <LawyerBenefits />
      <Support />
      <Flow />
      <FAQ />
      <CTA />
      
      <Footer />
    </div>
  );
};

export default KyoyuMeigi;
